// `useExtensionPresence()` — best-effort probe for the Barel MV3 extension.
//
// Wraps `chrome.runtime.sendMessage` with a 1500ms hard timeout. Returns
// `'unknown'` until the probe resolves, `'present'` on a clean response,
// `'absent'` on any timeout / lastError / non-Chrome browser.
//
// US-176 / US-177 — drives the "Install extension" CTA fallback in the
// `ConnectFlow` extension path.

import { useEffect, useState } from 'react';

export type ExtensionPresence = 'unknown' | 'present' | 'absent';

interface ChromeRuntime {
  sendMessage?: (
    extensionId: string,
    message: unknown,
    callback?: (response: unknown) => void,
  ) => void;
  lastError?: { message?: string };
}

interface ChromeApi {
  runtime?: ChromeRuntime;
}

declare global {
  interface Window {
    chrome?: ChromeApi;
  }
}

/**
 * Probe the Barel extension. Falls back to `'absent'` after 1500 ms or
 * when `chrome.runtime` is missing (Firefox, Safari, server).
 */
export function useExtensionPresence(extensionId?: string): ExtensionPresence {
  const [presence, setPresence] = useState<ExtensionPresence>('unknown');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const chrome = window.chrome;
    if (!chrome?.runtime?.sendMessage || !extensionId) {
      setPresence('absent');
      return;
    }
    let settled = false;
    const finish = (next: ExtensionPresence) => {
      if (settled) return;
      settled = true;
      setPresence(next);
    };
    try {
      chrome.runtime.sendMessage(extensionId, { type: 'barel:ping' }, () => {
        if (chrome.runtime?.lastError) {
          finish('absent');
          return;
        }
        finish('present');
      });
    } catch {
      finish('absent');
    }
    const timeout = window.setTimeout(() => finish('absent'), 1500);
    return () => window.clearTimeout(timeout);
  }, [extensionId]);

  return presence;
}
